/**
 * Which sales cycle a date falls in.
 *
 * Documents carry no cycle id of their own, so a screen that wants to show one
 * derives it: the cycle whose inclusive `[startDate, endDate]` window holds the
 * document's date.
 *
 * ⛔ **BARE `YYYY-MM-DD` STRINGS, COMPARED AS STRINGS — never `new Date()`.**
 * A date-only string parsed as a `Date` is midnight UTC and lands on the day
 * before west of Greenwich, and `new Date('2026-02-30')` rolls over instead of
 * failing. Zero-padded ISO dates order correctly as text, which is all a
 * window test needs.
 *
 * ⚠️ **Never assume a cycle from its id.** Cycle ids are non-contiguous; only
 * the dates say which cycle a day belongs to. Cycles run 1 Apr – 31 Mar today,
 * but nothing here relies on that.
 *
 * An ISO instant (`2026-05-04T10:22:00.000Z`) is read by its leading calendar
 * day, which is the UTC day — the app works in UTC.
 */

/** The fields this needs from a cycle — `Cycle` from `@/lib/types` fits. */
export interface CycleWindow {
  readonly id: number;
  readonly cycleName: string;
  /** Plain `YYYY-MM-DD`. */
  readonly startDate: string;
  /** Plain `YYYY-MM-DD`, inclusive. */
  readonly endDate: string;
}

export interface CycleOfDate {
  readonly id: number;
  readonly name: string;
}

const DAY = /^(\d{4}-\d{2}-\d{2})/;

/** The leading `YYYY-MM-DD` of a date or an instant, or `null`. */
export function calendarDayOf(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const match = DAY.exec(value.trim());
  return match?.[1] ?? null;
}

/**
 * The cycle holding `date`, or `null` when there is no date, no cycles, or no
 * cycle window contains it. Should two windows ever overlap, the one that
 * started later wins, so the answer does not depend on the list's order.
 */
export function cycleOfDate(
  date: string | null | undefined,
  cycles: readonly CycleWindow[] | null | undefined,
): CycleOfDate | null {
  const day = calendarDayOf(date);
  if (day === null || !cycles) return null;
  let found: CycleWindow | null = null;
  for (const cycle of cycles) {
    const start = calendarDayOf(cycle.startDate);
    const end = calendarDayOf(cycle.endDate);
    if (start === null || end === null) continue;
    if (day < start || day > end) continue;
    if (found === null || start > found.startDate) found = cycle;
  }
  return found === null ? null : { id: found.id, name: found.cycleName };
}
